import { escapeAttribute, escapeHtml, getElement, showError } from "./dom";
import { formatEuroAmount, formatField, formatOdooDate, getTelUrl, getWebsiteUrl, hasManyToManyValues, labelFromKey } from "./formatters";
import { collectLookupAddresses } from "./officeContext";
import { getOdooRecordUrl } from "./odooUrls";
export function renderResults(results, actions) {
    getElement("results").innerHTML = [
        renderDuplicateCandidates(results),
        renderRecordGroup("Companies", results.companies),
        renderRecordGroup("Contacts", results.contacts),
        renderRecordGroup("Opportunities", results.opportunities ?? []),
        renderSalesOrdersGroup(results),
        renderRecordGroup("Client Invoices", results.clientInvoices),
        renderRecordGroup("Purchase Orders", results.purchaseOrders),
        renderRecordGroup("Bills", results.bills)
    ].join("");
    bindCreateSalesOrderActions(actions);
    bindMarketingSwitches(actions);
    bindEmailComposeActions(actions);
    bindContactMenus(actions);
    bindSmartMergeActions(actions);
    void populateTagSelectors(actions);
}
export function hasNoPartnerMatches(results) {
    return results.companies.length === 0 && results.contacts.length === 0;
}
export function showNoPartnerMatchNotice(email) {
    const lookupAddresses = collectLookupAddresses(email);
    const lookupText = lookupAddresses.length ? lookupAddresses.join(", ") : "this email";
    getElement("results").innerHTML =
        `<div class="notice-card">No company or contact found for <strong>${escapeHtml(lookupText)}</strong>. Use <strong>Extract company/contact</strong> only if this email should become an Odoo record.</div>`;
}
export function showNoExternalLookupAddressNotice() {
    getElement("results").innerHTML =
        '<div class="notice-card">No external email address found for Odoo lookup. Internal @seema.de addresses are ignored.</div>';
}
function renderRecordGroup(title, records) {
    const rows = records.length === 0
        ? '<p class="empty">No matching records found.</p>'
        : records.map((record) => renderRecord(record)).join("");
    return `
    <article class="group">
      <h2>${escapeHtml(title)} <span>${records.length}</span></h2>
      ${rows}
    </article>
  `;
}
function renderSalesOrdersGroup(results) {
    const partnerId = getSalesOrderPartnerId(results);
    const rows = results.salesOrders.length === 0
        ? '<p class="empty">No matching records found.</p>'
        : results.salesOrders.map((record) => renderRecord(record)).join("");
    const createButton = partnerId
        ? `<button type="button" class="section-add-button create-sales-order-button" data-partner-id="${partnerId}" title="Create sales order in Odoo" aria-label="Create sales order in Odoo">+</button>`
        : "";
    return `
    <article class="group">
      <div class="section-heading">
        <h2>Sales Orders <span>${results.salesOrders.length}</span></h2>
        ${createButton}
      </div>
      ${rows}
    </article>
  `;
}
function getSalesOrderPartnerId(results) {
    const partner = (results.primaryContact || undefined) ||
        results.contacts[0] ||
        (results.primaryCompany || undefined) ||
        results.companies[0];
    return partner?.id;
}
function renderDuplicateCandidates(results) {
    const duplicates = results.duplicateCandidates ?? [];
    if (duplicates.length === 0) {
        return "";
    }
    const suggestion = results.mergeSuggestion || undefined;
    const mergeButton = suggestion && suggestion.sourceIds.length
        ? `<button type="button" class="smart-merge-button" data-destination-id="${suggestion.destinationId}" data-source-ids="${escapeAttribute(suggestion.sourceIds.join(","))}">Smart merge suggested duplicates</button>`
        : "";
    const rows = duplicates
        .map((record) => {
        const recordUrl = getOdooRecordUrl(record);
        const email = formatField(record.fields.email);
        const parent = formatField(record.fields.parent_id);
        return `
        <li>
          <a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(record.name)}</a>
          ${email ? `<span>${escapeHtml(email)}</span>` : ""}
          ${parent ? `<span>${escapeHtml(parent)}</span>` : ""}
        </li>
      `;
    })
        .join("");
    return `
    <article class="group duplicate-group">
      <h2>Possible Duplicates <span>${duplicates.length}</span></h2>
      <p class="summary">Odoo smart duplicate logic found records that may need review.</p>
      <ul>${rows}</ul>
      ${mergeButton}
    </article>
  `;
}
function renderRecord(record) {
    if (record.model === "crm.lead") {
        return renderOpportunityRecord(record);
    }
    if (record.model === "sale.order") {
        return renderSalesOrderRecord(record);
    }
    if (record.model === "res.partner" && !Boolean(record.fields.is_company)) {
        return renderContactRecord(record);
    }
    if (record.model === "res.partner" && Boolean(record.fields.is_company)) {
        return renderCompanyRecord(record);
    }
    const fields = getVisibleFields(record)
        .map(([key, value]) => `<li><strong>${escapeHtml(labelFromKey(key))}</strong>${renderFieldValue(record, key, value)}</li>`)
        .join("");
    const recordUrl = getOdooRecordUrl(record);
    return `
    <div class="record">
      <h3><a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(record.name)}</a></h3>
      <p>${escapeHtml(record.model)} #${record.id}</p>
      <ul>${fields}</ul>
    </div>
  `;
}
function renderOpportunityRecord(record) {
    const recordUrl = getOdooRecordUrl(record);
    const customer = formatField(record.fields.partner_id);
    const contact = formatField(record.fields.contact_name);
    const stage = formatField(record.fields.stage_id);
    const expectedRevenue = formatEuroAmount(record.fields.expected_revenue);
    const probability = formatPercentage(record.fields.probability);
    const deadline = formatOdooDate(record.fields.date_deadline);
    return `
    <div class="record opportunity-record">
      <h3><a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(record.name)}</a></h3>
      <ul class="opportunity-details">
        ${customer ? `<li><strong>Customer</strong>${escapeHtml(customer)}</li>` : ""}
        ${contact ? `<li><strong>Contact</strong>${escapeHtml(contact)}</li>` : ""}
        ${stage ? `<li><strong>Stage</strong>${renderStateBadge(stage)}</li>` : ""}
        ${expectedRevenue ? `<li><strong>Expected</strong>${escapeHtml(expectedRevenue)}</li>` : ""}
        ${probability ? `<li><strong>Probability</strong>${escapeHtml(probability)}</li>` : ""}
        ${deadline ? `<li><strong>Deadline</strong>${escapeHtml(deadline)}</li>` : ""}
      </ul>
    </div>
  `;
}
function renderCompanyRecord(record) {
    const recordUrl = getOdooRecordUrl(record);
    const companyName = formatField(record.fields.name) || record.name;
    const marketingEnabled = record.fields.marketing_enabled === true;
    const hasTags = hasManyToManyValues(record.fields.category_id);
    const actions = [
        renderRecordAction("email", record.fields.email),
        renderRecordAction("phone", record.fields.phone),
        renderRecordAction("mobile", record.fields.mobile),
        renderRecordAction("website", record.fields.website)
    ]
        .filter(Boolean)
        .join("");
    const city = formatField(record.fields.city);
    return `
    <div class="record compact-record">
      <div class="record-line">
        <h3><a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(companyName)}</a></h3>
        ${actions ? `<div class="record-actions">${actions}</div>` : ""}
      </div>
      ${hasTags ? "" : renderMissingTagCheck(record.id)}
      ${city ? `<p class="compact-meta">${renderIcon("location")}${escapeHtml(city)}</p>` : ""}
      <label class="marketing-switch${marketingEnabled ? " is-enabled" : ""}">
        <input type="checkbox" role="switch" data-company-id="${record.id}" ${marketingEnabled ? "checked disabled" : ""} />
        <span>Enable marketing</span>
      </label>
    </div>
  `;
}
function renderMissingTagCheck(companyId) {
    return `
    <div class="quality-check" data-company-id="${companyId}">
      <span>Missing company tag</span>
      <select class="tag-selector" data-company-id="${companyId}" aria-label="Select company tag">
        <option value="">Loading tags...</option>
      </select>
    </div>
  `;
}
function renderContactRecord(record) {
    const recordUrl = getOdooRecordUrl(record);
    const contactName = formatField(record.fields.name) || record.name;
    const contactFunction = formatField(record.fields.function);
    const decisionMaker = record.fields.decision_maker === true;
    const actions = [
        renderRecordAction("email", record.fields.email),
        renderRecordAction("phone", record.fields.phone),
        renderRecordAction("mobile", record.fields.mobile)
    ]
        .filter(Boolean)
        .join("");
    return `
    <div class="record compact-record contact-record${decisionMaker ? " is-decision-maker" : ""}">
      <div class="record-line">
        <h3><a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(contactName)}</a></h3>
        ${actions ? `<div class="record-actions">${actions}</div>` : ""}
        <span class="decision-maker-icon${decisionMaker ? "" : " is-hidden"}" title="Decision maker">${renderIcon("crown")}</span>
        <details class="contact-menu">
          <summary aria-label="Contact actions">⋯</summary>
          <button type="button" data-contact-id="${record.id}" ${decisionMaker ? "disabled" : ""}>Set as decision maker</button>
        </details>
      </div>
      ${contactFunction ? `<p class="contact-function">${escapeHtml(contactFunction)}</p>` : ""}
    </div>
  `;
}
function renderRecordAction(type, value) {
    const formattedValue = formatField(value);
    if (!formattedValue) {
        return "";
    }
    const config = {
        email: {
            href: "#",
            label: `Email ${formattedValue}`,
            icon: "email",
            value: formattedValue
        },
        mobile: {
            href: getTelUrl(formattedValue),
            label: `Mobile ${formattedValue}`,
            icon: "mobile",
            value: ""
        },
        phone: {
            href: getTelUrl(formattedValue),
            label: `Phone ${formattedValue}`,
            icon: "phone",
            value: ""
        },
        website: {
            href: getWebsiteUrl(formattedValue),
            label: `Website ${formattedValue}`,
            icon: "website",
            value: ""
        }
    };
    const action = config[type];
    const dataEmail = type === "email" ? ` data-email="${escapeAttribute(action.value)}"` : "";
    return `<a class="record-action record-action-${type}" href="${escapeAttribute(action.href)}"${dataEmail} aria-label="${escapeAttribute(action.label)}" title="${escapeAttribute(action.label)}">${renderIcon(action.icon)}</a>`;
}
function renderIcon(icon) {
    const icons = {
        crown: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19h14l1-10-4.5 3.2L12 5l-3.5 7.2L4 9l1 10Zm1.2 2-.2-2h12l-.2 2H6.2Z"/></svg>',
        email: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16v12H4V6Zm1.8 2 6.2 4.4L18.2 8H5.8Zm12.7 8V9.7l-6.5 4.6-6.5-4.6V16h13Z"/></svg>',
        location: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z"/></svg>',
        mobile: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 2h8c1.1 0 2 .9 2 2v16c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2Zm0 3v14h8V5H8Zm3 15v1h2v-1h-2Z"/></svg>',
        phone: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.6 3.5 9.4 3l2 5.2-2 1.2c.9 1.8 2.4 3.3 4.2 4.2l1.2-2 5.2 2-.5 2.8c-.2 1.1-1.1 1.8-2.2 1.7C10.9 17.7 6.3 13.1 5.9 6.7c-.1-1.1.6-2 1.7-2.2Z"/></svg>',
        website: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.9 9h-3.2a15.8 15.8 0 0 0-1.2-5A8.1 8.1 0 0 1 18.9 11ZM12 4.1c.7 1 1.4 3.1 1.7 6.9h-3.4c.3-3.8 1-5.9 1.7-6.9ZM4.2 13h3.9c.1 1.8.4 3.4.8 4.8A8 8 0 0 1 4.2 13Zm3.9-2H4.2A8 8 0 0 1 8.9 6.2 17 17 0 0 0 8.1 11Zm3.9 8.9c-.7-1-1.4-3.1-1.7-6.9h3.4c-.3 3.8-1 5.9-1.7 6.9Zm3.1-2.1c.4-1.4.7-3 .8-4.8h3.9a8 8 0 0 1-4.7 4.8Z"/></svg>'
    };
    return icons[icon];
}
function renderSalesOrderRecord(record) {
    const recordUrl = getOdooRecordUrl(record);
    const clientOrderRef = formatField(record.fields.client_order_ref) || "No client order reference";
    return `
    <div class="record sales-order-record">
      <h3><a href="${escapeAttribute(recordUrl)}" target="_blank" rel="noreferrer">${escapeHtml(record.name)}</a></h3>
      <p class="client-order-ref">${escapeHtml(clientOrderRef)}</p>
      <ul class="sales-order-details">
        <li>${escapeHtml(formatOdooDate(record.fields.date_order))}</li>
        <li>${escapeHtml(formatEuroAmount(record.fields.amount_total))}</li>
        <li>${renderStateBadge(formatField(record.fields.state))}</li>
      </ul>
    </div>
  `;
}
function getVisibleFields(record) {
    if (record.model === "sale.order") {
        return ["client_order_ref", "date_order", "amount_total", "state"]
            .map((key) => [key, record.fields[key]])
            .filter(([key, value]) => key === "client_order_ref" || key === "state" || formatField(value));
    }
    return Object.entries(record.fields)
        .filter(([key]) => key !== "id" && key !== "display_name")
        .slice(0, 6);
}
function bindCreateSalesOrderActions(actions) {
    document.querySelectorAll(".create-sales-order-button").forEach((button) => {
        button.addEventListener("click", () => {
            const partnerId = Number(button.dataset.partnerId);
            if (!Number.isFinite(partnerId) || partnerId <= 0) {
                showError(new Error("Missing partner id for sales order creation."));
                return;
            }
            actions.onCreateSalesOrder(partnerId, button);
        });
    });
}
function bindMarketingSwitches(actions) {
    document.querySelectorAll(".marketing-switch input").forEach((input) => {
        input.addEventListener("change", () => {
            if (!input.checked) {
                return;
            }
            const companyId = Number(input.dataset.companyId);
            if (!Number.isFinite(companyId)) {
                input.checked = false;
                showError(new Error("Missing company id for marketing action."));
                return;
            }
            actions.onEnableMarketing(companyId, input);
        });
    });
}
function bindEmailComposeActions(actions) {
    document.querySelectorAll(".record-action-email").forEach((link) => {
        link.addEventListener("click", (event) => {
            event.preventDefault();
            const emailAddress = link.dataset.email;
            if (!emailAddress) {
                return;
            }
            actions.onEmailCompose(emailAddress);
        });
    });
}
function bindContactMenus(actions) {
    document.querySelectorAll(".contact-menu button[data-contact-id]").forEach((button) => {
        button.addEventListener("click", () => {
            const contactId = Number(button.dataset.contactId);
            if (!Number.isFinite(contactId)) {
                showError(new Error("Missing contact id for decision maker action."));
                return;
            }
            actions.onSetDecisionMaker(contactId, button);
        });
    });
}
function bindSmartMergeActions(actions) {
    document.querySelectorAll(".smart-merge-button").forEach((button) => {
        button.addEventListener("click", () => {
            const destinationId = Number(button.dataset.destinationId);
            const sourceIds = (button.dataset.sourceIds ?? "")
                .split(",")
                .map((value) => Number(value))
                .filter((value) => Number.isFinite(value) && value > 0);
            if (!Number.isFinite(destinationId) || sourceIds.length === 0) {
                showError(new Error("Missing smart merge destination or sources."));
                return;
            }
            actions.onMergeDuplicates(destinationId, sourceIds, button);
        });
    });
}
async function populateTagSelectors(actions) {
    const selectors = Array.from(document.querySelectorAll(".tag-selector"));
    if (selectors.length === 0) {
        return;
    }
    try {
        const categories = await actions.loadPartnerCategories();
        selectors.forEach((select) => {
            select.innerHTML = [
                '<option value="">Select tag...</option>',
                ...[...categories]
                    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" }))
                    .map((category) => `<option value="${category.id}">${escapeHtml(category.name)}</option>`)
            ].join("");
            select.addEventListener("change", () => {
                const categoryId = Number(select.value);
                const companyId = Number(select.dataset.companyId);
                if (!Number.isFinite(categoryId) || !Number.isFinite(companyId) || categoryId <= 0) {
                    return;
                }
                actions.onAddCompanyTag(companyId, categoryId, select);
            });
        });
    }
    catch (error) {
        selectors.forEach((select) => {
            select.innerHTML = '<option value="">Could not load tags</option>';
            select.disabled = true;
        });
        showError(error);
    }
}
function renderFieldValue(record, key, value) {
    const formattedValue = formatField(value);
    if (!formattedValue) {
        return "";
    }
    if (record.model === "sale.order" && key === "amount_total") {
        return escapeHtml(formatEuroAmount(value));
    }
    if (record.model === "sale.order" && key === "state") {
        return renderStateBadge(formattedValue);
    }
    if (key === "phone" || key === "mobile") {
        return `<a href="${escapeAttribute(getTelUrl(formattedValue))}">${escapeHtml(formattedValue)}</a>`;
    }
    return escapeHtml(formattedValue);
}
function formatPercentage(value) {
    const percentage = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(percentage)) {
        return "";
    }
    return `${Math.round(percentage)}%`;
}
function renderStateBadge(state) {
    return `<span class="state-badge state-${escapeAttribute(state.toLowerCase().replace(/[^a-z0-9]+/g, "-"))}">${escapeHtml(labelFromKey(state))}</span>`;
}
